function buildUrl(endpoint, baseUrl) {
  let url = `${baseUrl}${endpoint.path}`;
  (endpoint.parameters || [])
    .filter((p) => p.location === "path")
    .forEach((p) => {
      url = url.replace(`{${p.name}}`, p.example || `:${p.name}`);
    });
  return url;
}

function queryParams(endpoint) {
  return (endpoint.parameters || []).filter(
    (p) => p.location === "query" && p.example !== undefined
  );
}

function bodyParams(endpoint) {
  return (endpoint.parameters || []).filter((p) => p.location === "body");
}

function buildBodyObject(params) {
  const body = {};
  params.forEach((p) => {
    body[p.name] = p.example !== undefined ? p.example : `<${p.type}>`;
  });
  return body;
}

function needsAuth(endpoint) {
  return endpoint.auth === "required" || endpoint.auth === "admin";
}

function hasBody(endpoint) {
  return (
    bodyParams(endpoint).length > 0 &&
    ["POST", "PUT", "PATCH"].includes(endpoint.method)
  );
}

export function generateShell(endpoint, baseUrl) {
  const url = buildUrl(endpoint, baseUrl);
  const qp = queryParams(endpoint);

  let queryString = "";
  if (qp.length > 0) {
    queryString = "?" + qp.map((p) => `${encodeURIComponent(p.name)}=${encodeURIComponent(p.example)}`).join("&");
  }

  const lines = [`curl -X ${endpoint.method} \\`];
  lines.push(`  "${url}${queryString}" \\`);

  if (needsAuth(endpoint)) {
    lines.push(`  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\`);
  }
  lines.push(`  -H "Content-Type: application/json"`);

  if (hasBody(endpoint)) {
    const body = buildBodyObject(bodyParams(endpoint));
    lines[lines.length - 1] += " \\";
    lines.push(`  -d '${JSON.stringify(body, null, 2)}'`);
  }

  return lines.join("\n");
}

export function generateNodeJs(endpoint, baseUrl) {
  const url = buildUrl(endpoint, baseUrl);
  const qp = queryParams(endpoint);
  const bp = bodyParams(endpoint);

  let fullUrl = url;
  if (qp.length > 0) {
    fullUrl += "?" + qp.map((p) => `${encodeURIComponent(p.name)}=${encodeURIComponent(p.example)}`).join("&");
  }

  const lines = [];
  lines.push(`const response = await fetch("${fullUrl}", {`);
  lines.push(`  method: "${endpoint.method}",`);
  lines.push(`  headers: {`);

  if (needsAuth(endpoint)) {
    lines.push(`    "Authorization": "Bearer YOUR_ACCESS_TOKEN",`);
  }
  lines.push(`    "Content-Type": "application/json",`);
  lines.push(`  },`);

  if (hasBody(endpoint)) {
    const body = buildBodyObject(bp);
    lines.push(`  body: JSON.stringify(${JSON.stringify(body, null, 4).replace(/\n/g, "\n  ")}),`);
  }

  lines.push(`});`);
  lines.push(``);
  lines.push(`const data = await response.json();`);
  lines.push(`console.log(data);`);

  return lines.join("\n");
}

export function generateRuby(endpoint, baseUrl) {
  const url = buildUrl(endpoint, baseUrl);
  const qp = queryParams(endpoint);
  const bp = bodyParams(endpoint);

  let fullUrl = url;
  if (qp.length > 0) {
    fullUrl += "?" + qp.map((p) => `${encodeURIComponent(p.name)}=${encodeURIComponent(p.example)}`).join("&");
  }

  const lines = [];
  lines.push(`require "net/http"`);
  lines.push(`require "json"`);
  lines.push(``);
  lines.push(`uri = URI("${fullUrl}")`);

  const methodClass = {
    GET: "Net::HTTP::Get",
    POST: "Net::HTTP::Post",
    PUT: "Net::HTTP::Put",
    PATCH: "Net::HTTP::Patch",
    DELETE: "Net::HTTP::Delete",
  }[endpoint.method] || "Net::HTTP::Get";

  lines.push(`request = ${methodClass}.new(uri)`);

  if (needsAuth(endpoint)) {
    lines.push(`request["Authorization"] = "Bearer YOUR_ACCESS_TOKEN"`);
  }
  lines.push(`request["Content-Type"] = "application/json"`);

  if (hasBody(endpoint)) {
    const body = buildBodyObject(bp);
    lines.push(`request.body = '${JSON.stringify(body, null, 2)}'`);
  }

  lines.push(``);
  lines.push(`response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|`);
  lines.push(`  http.request(request)`);
  lines.push(`end`);
  lines.push(``);
  lines.push(`puts JSON.parse(response.body)`);

  return lines.join("\n");
}

export function generatePhp(endpoint, baseUrl) {
  const url = buildUrl(endpoint, baseUrl);
  const qp = queryParams(endpoint);
  const bp = bodyParams(endpoint);

  let fullUrl = url;
  if (qp.length > 0) {
    fullUrl += "?" + qp.map((p) => `${encodeURIComponent(p.name)}=${encodeURIComponent(p.example)}`).join("&");
  }

  const lines = [];
  lines.push(`<?php`);
  lines.push(``);
  lines.push(`$ch = curl_init();`);
  lines.push(``);
  lines.push(`curl_setopt($ch, CURLOPT_URL, "${fullUrl}");`);
  lines.push(`curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);`);
  lines.push(`curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "${endpoint.method}");`);
  lines.push(``);
  lines.push(`$headers = [`);
  if (needsAuth(endpoint)) {
    lines.push(`    "Authorization: Bearer YOUR_ACCESS_TOKEN",`);
  }
  lines.push(`    "Content-Type: application/json",`);
  lines.push(`];`);
  lines.push(`curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);`);

  if (hasBody(endpoint)) {
    const body = buildBodyObject(bp);
    lines.push(``);
    lines.push(`$body = '${JSON.stringify(body, null, 4).replace(/\n/g, "\n")}';`);
    lines.push(`curl_setopt($ch, CURLOPT_POSTFIELDS, $body);`);
  }

  lines.push(``);
  lines.push(`$response = curl_exec($ch);`);
  lines.push(`curl_close($ch);`);
  lines.push(``);
  lines.push(`$data = json_decode($response, true);`);
  lines.push(`print_r($data);`);

  return lines.join("\n");
}

export function generatePython(endpoint, baseUrl) {
  const url = buildUrl(endpoint, baseUrl);
  const qp = queryParams(endpoint);
  const bp = bodyParams(endpoint);

  const lines = [];
  lines.push(`import requests`);
  lines.push(``);
  lines.push(`url = "${url}"`);

  if (needsAuth(endpoint)) {
    lines.push(`headers = {`);
    lines.push(`    "Authorization": "Bearer YOUR_ACCESS_TOKEN",`);
    lines.push(`    "Content-Type": "application/json",`);
    lines.push(`}`);
  } else {
    lines.push(`headers = {"Content-Type": "application/json"}`);
  }

  if (qp.length > 0) {
    lines.push(``);
    lines.push(`params = {`);
    qp.forEach((p) => {
      const val = typeof p.example === "string" ? `"${p.example}"` : p.example;
      lines.push(`    "${p.name}": ${val},`);
    });
    lines.push(`}`);
  }

  if (hasBody(endpoint)) {
    const body = buildBodyObject(bp);
    lines.push(``);
    lines.push(`data = ${JSON.stringify(body, null, 4)}`);
  }

  lines.push(``);

  const methodName = endpoint.method.toLowerCase();
  let callArgs = `url, headers=headers`;
  if (qp.length > 0) callArgs += `, params=params`;
  if (hasBody(endpoint)) callArgs += `, json=data`;

  lines.push(`response = requests.${methodName}(${callArgs})`);
  lines.push(`print(response.json())`);

  return lines.join("\n");
}

export function generateCodeExamples(endpoint, baseUrl) {
  return {
    shell: generateShell(endpoint, baseUrl),
    nodejs: generateNodeJs(endpoint, baseUrl),
    ruby: generateRuby(endpoint, baseUrl),
    php: generatePhp(endpoint, baseUrl),
    python: generatePython(endpoint, baseUrl),
  };
}
